import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CurrentOrPreviousGroup, Member, School } from '../../../../../../api/groups';
import { FilterSearchParam } from '../../../../../../shared-components/elementis/types/FilterSearchParam';
import { useResultsDataContext } from '../../ResultsDataContext';
import groupBy from 'lodash/groupBy';
import mapValues from 'lodash/mapValues';
import TABS, { Tab } from '../../../../../../shared-components/elementis/types/ResultsPageTabs';
import { buildRelativePath } from '../../../../../../hooks/useRoutes';
import { SupportedUserLanguage } from '@pelckmans/business-components/context/LanguageContext';

export type GroupAndSelectedMemberId = [string, string];

export function createLinkWithMembersToFilter(members: string[], groupIds: string[], language: SupportedUserLanguage) {
  const url = buildRelativePath('MATERIAL.RESULTS.BY_STUDENT', language);

  const search = new URLSearchParams(window.location.search);

  search.set(FilterSearchParam.Groups, groupIds.join(','));
  search.set(FilterSearchParam.Members, members.join(','));

  return `../${url}?${search.toString()}`;
}

function processConcatenatedMemberIds(
  concatenatedMemberIds: string | null,
): Record<string, GroupAndSelectedMemberId[]> | null {
  if (!concatenatedMemberIds) return null;

  const groupIdAndMemberId = concatenatedMemberIds
    .split(',')
    .map(concatenatedId => concatenatedId.split(';')) as GroupAndSelectedMemberId[];

  return groupBy(groupIdAndMemberId, ([groupId]) => groupId);
}

export function useSelectedMembers({
  selectedGroups,
  selectedTab,
  selectedSchool,
}: {
  selectedGroups: CurrentOrPreviousGroup[];
  selectedTab: Tab;
  selectedSchool?: School | null;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { members: membersPerGroup } = useResultsDataContext();

  const concatenatedMemberIds = searchParams.get(FilterSearchParam.Members);
  const spMemberIdsPerGroup = processConcatenatedMemberIds(concatenatedMemberIds);

  const selectedMembersPerGroup = useMemo(() => {
    if (!spMemberIdsPerGroup) return membersPerGroup;

    return mapValues(spMemberIdsPerGroup, (groupAndMemberIds: GroupAndSelectedMemberId[]) => {
      return groupAndMemberIds
        .map(([groupId, memberId]) => membersPerGroup[groupId]?.find(member => member.id === memberId))
        .filter(Boolean);
    }) as Record<string, Member[]>;
  }, [membersPerGroup, spMemberIdsPerGroup]);

  const totalMembersOfGroupsCount = useMemo(() => {
    if (selectedTab === TABS.STUDENT) {
      return membersPerGroup[selectedGroups[0]?.id]?.length || 0;
    }
    return selectedGroups.reduce((sum, group) => {
      if (group.school.id !== selectedSchool?.id) return sum;
      return (sum += group.acceptedMemberCount);
    }, 0);
  }, [membersPerGroup, selectedGroups, selectedSchool?.id, selectedTab]);

  const setSelectedMembers = useCallback(
    (members: string[]) => {
      setSearchParams(prev => {
        const newParams = new URLSearchParams(prev);

        if (members.length === 0 || totalMembersOfGroupsCount === 0) {
          newParams.delete(FilterSearchParam.Members);
        } else if (members.length === 1) {
          newParams.set(FilterSearchParam.Members, members[0]);
        } else if (members.length === totalMembersOfGroupsCount) {
          newParams.delete(FilterSearchParam.Members);
        } else {
          newParams.set(FilterSearchParam.Members, members.join(','));
        }

        return newParams;
      });
    },
    [setSearchParams, totalMembersOfGroupsCount],
  );

  return { selectedMembers: selectedMembersPerGroup, setSelectedMembers, spMemberIdsPerGroup };
}
